import {
    type BlockQuestion,
    type ContactBlockConfig,
    type OptionalCoreContactFields,
    type TemplateFormFieldUpdate,
} from '@common/types/templateFormFields';
import type {
    HiddenContactFields,
    RequiredContactFields,
    TemplateFormLanguage,
} from '@common/types/templateForms';
import {
    groupQuestionsWithConditionals,
    removeQuestionWithConditionals,
} from '@common/utils/conditionalQuestions';
import {
    moveBlockQuestion,
    toBlockQuestion,
    upsertBlockQuestions,
} from '@common/utils/contactBlock';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { useState } from 'react';
import ContactFieldsEditor from '@/pages/QuoteIntake/IntakeOnlySetup/ContactFieldsEditor';
import { errorSnackbar } from '@/utils/snackbar';
import NewQuestionsDrawer from './NewQuestionsDrawer';

/** A block without contact fields needs at least one question, or its entries would be empty. */
export function isContactBlockConfigValid(config: ContactBlockConfig): boolean {
    return (
        !!config.entryLabel.trim() &&
        (!config.hideContactFields || (config.questions?.length ?? 0) > 0)
    );
}

interface Props {
    config: ContactBlockConfig;
    language: TemplateFormLanguage;
    practiceArea?: string | null;
    matterType?: string | null;
    disabled?: boolean;
    onChange: (config: ContactBlockConfig) => void;
}

export default function ContactBlockConfigEditor({
    config,
    language,
    practiceArea,
    matterType,
    disabled,
    onChange,
}: Props) {
    const theme = useTheme();
    const [questionDrawer, setQuestionDrawer] = useState<{
        isOpen: boolean;
        question?: BlockQuestion;
    }>({
        isOpen: false,
    });
    const questions = config.questions ?? [];
    const groups = groupQuestionsWithConditionals(questions);
    const hiddenFields = config.hiddenFields ?? {};
    const requiredFields = config.requiredFields ?? {};

    const update = (patch: Partial<ContactBlockConfig>) => onChange({ ...config, ...patch });

    const handleHiddenFieldChange = (field: keyof HiddenContactFields, checked: boolean) => {
        update({
            hiddenFields: { ...hiddenFields, [field]: checked },
            ...(checked && { requiredFields: { ...requiredFields, [field]: false } }),
        });
    };

    const handleRequiredFieldChange = (field: keyof RequiredContactFields, checked: boolean) => {
        update({
            requiredFields: { ...requiredFields, [field]: checked },
            ...(checked && { hiddenFields: { ...hiddenFields, [field]: false } }),
        });
    };

    const handleOptionalCoreFieldChange = (
        field: keyof OptionalCoreContactFields,
        optional: boolean,
    ) => {
        update({
            optionalCoreFields: { ...config.optionalCoreFields, [field]: optional },
            ...(!optional && { hiddenFields: { ...hiddenFields, [field]: false } }),
        });
    };

    const handleHiddenCoreFieldChange = (
        field: keyof OptionalCoreContactFields,
        hidden: boolean,
    ) => {
        update({
            hiddenFields: { ...hiddenFields, [field]: hidden },
            ...(hidden && {
                optionalCoreFields: { ...config.optionalCoreFields, [field]: true },
            }),
        });
    };

    const closeQuestionDrawer = () => setQuestionDrawer({ isOpen: false });

    const handleQuestionConfirm = (
        fieldData: TemplateFormFieldUpdate | TemplateFormFieldUpdate[],
    ) => {
        const incoming = (Array.isArray(fieldData) ? fieldData : [fieldData]).map(toBlockQuestion);
        if (incoming.some((question) => !question.title.trim())) {
            errorSnackbar('Question cannot be empty!');
            return;
        }
        const editingName = questionDrawer.question?.name;
        const existingNames = new Set(questions.map((question) => question.name));
        if (
            incoming.some(
                (question) => question.name !== editingName && existingNames.has(question.name),
            )
        ) {
            errorSnackbar('This question already exists!');
            return;
        }
        update({ questions: upsertBlockQuestions(questions, incoming) });
        closeQuestionDrawer();
    };

    const handleQuestionDelete = (name: string) => {
        update({ questions: removeQuestionWithConditionals(questions, name).questions });
    };

    const renderQuestionRow = (
        question: BlockQuestion,
        actions: { canMoveUp?: boolean; canMoveDown?: boolean; isConditional?: boolean },
    ) => (
        <Stack
            key={question.name}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
                paddingY: '6px',
                paddingLeft: actions.isConditional ? '32px' : '8px',
                paddingRight: '8px',
                borderTop: `1px solid ${theme.palette.neutral.borderColor}`,
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {actions.isConditional && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        If &apos;{String(question.questionSchema.conditionValue ?? '')}&apos;:
                    </Typography>
                )}
                <Typography variant="body2" noWrap>
                    {question.title}
                    {question.questionValidationSchema?.required ? ' *' : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {question.questionSchema.type}
                </Typography>
            </Box>
            {!actions.isConditional && (
                <>
                    <IconButton
                        size="small"
                        aria-label="Move question up"
                        disabled={disabled || !actions.canMoveUp}
                        onClick={() =>
                            update({ questions: moveBlockQuestion(questions, question.name, -1) })
                        }
                    >
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label="Move question down"
                        disabled={disabled || !actions.canMoveDown}
                        onClick={() =>
                            update({ questions: moveBlockQuestion(questions, question.name, 1) })
                        }
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                </>
            )}
            <IconButton
                size="small"
                aria-label="Edit question"
                disabled={disabled}
                onClick={() => setQuestionDrawer({ isOpen: true, question })}
            >
                <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
                size="small"
                aria-label="Delete question"
                disabled={disabled}
                onClick={() => handleQuestionDelete(question.name)}
            >
                <DeleteOutlineIcon fontSize="small" />
            </IconButton>
        </Stack>
    );

    return (
        <Stack spacing={3}>
            <TextField
                label="Entry label"
                placeholder="e.g. Director"
                helperText={
                    'Each entry is titled with this label and its number, e.g. "Director 2".'
                }
                value={config.entryLabel}
                disabled={disabled}
                error={!config.entryLabel.trim()}
                onChange={(event) => update({ entryLabel: event.target.value })}
            />
            <TextField
                label="Maximum entries (optional)"
                type="number"
                helperText="Leave blank to let clients add as many entries as they need."
                value={config.maxEntries ?? ''}
                disabled={disabled}
                inputProps={{ min: 1, step: 1 }}
                onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '') {
                        update({ maxEntries: undefined });
                        return;
                    }
                    const parsed = Number.parseInt(raw, 10);
                    if (Number.isNaN(parsed) || parsed < 1) return;
                    update({ maxEntries: parsed });
                }}
            />
            <TextField
                label="Entry description (optional)"
                helperText="Shown at the top of each entry."
                multiline
                minRows={2}
                value={config.description ?? ''}
                disabled={disabled}
                onChange={(event) => update({ description: event.target.value || undefined })}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={!config.hideContactFields}
                        disabled={disabled}
                        onChange={(event) =>
                            update({ hideContactFields: !event.target.checked || undefined })
                        }
                    />
                }
                label="Collect contact details for each entry"
            />
            {!config.hideContactFields && (
                <Box>
                    <ContactFieldsEditor
                        language={language}
                        hiddenFields={hiddenFields}
                        requiredFields={requiredFields}
                        optionalCoreFields={config.optionalCoreFields ?? {}}
                        onHiddenFieldChange={handleHiddenFieldChange}
                        onRequiredFieldChange={handleRequiredFieldChange}
                        onOptionalCoreFieldChange={handleOptionalCoreFieldChange}
                        onHiddenCoreFieldChange={handleHiddenCoreFieldChange}
                    />
                </Box>
            )}
            <Box>
                <Typography variant="h4" sx={{ mb: 0.5 }}>
                    Questions for each entry
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {config.hideContactFields
                        ? `Asked once per ${config.entryLabel.trim() || 'entry'}. Add at least one question.`
                        : `Asked once per ${config.entryLabel.trim() || 'entry'}, after the contact fields.`}
                </Typography>
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                        borderRadius: '8px',
                        '& > div:first-of-type > div:first-of-type': { borderTop: 'none' },
                    }}
                >
                    {groups.length === 0 && (
                        <Typography
                            variant="body2"
                            sx={{ padding: '12px', color: 'text.secondary' }}
                        >
                            No questions yet.
                        </Typography>
                    )}
                    {groups.map((group, index) => (
                        <Box key={group.question.name}>
                            {renderQuestionRow(group.question, {
                                canMoveUp: index > 0,
                                canMoveDown: index < groups.length - 1,
                            })}
                            {group.conditionals.map((conditional) =>
                                renderQuestionRow(conditional, { isConditional: true }),
                            )}
                        </Box>
                    ))}
                </Box>
                <Button
                    variant="text"
                    startIcon={<AddIcon />}
                    disabled={disabled}
                    onClick={() => setQuestionDrawer({ isOpen: true })}
                    sx={{ mt: 1, textTransform: 'none' }}
                >
                    Add question
                </Button>
            </Box>
            <NewQuestionsDrawer
                isOpen={questionDrawer.isOpen}
                formField={questionDrawer.question}
                previousQuestionOrder={0}
                practiceArea={practiceArea}
                matterType={matterType}
                language={language}
                isIntakeForm
                isNestedQuestion
                onConfirm={handleQuestionConfirm}
                onClose={closeQuestionDrawer}
            />
        </Stack>
    );
}
